import { useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

import useSnackbar from './useSnackbar'
import { extractErrorMessage } from '../utils/apitools'
import type {
  ChatCompletionMessageParam,
} from 'openai/resources/chat/completions'

// Comment: Simple hook that calls backend chat and can preload analysis
export const useChat = ({
  customer,
  job,
  initialMessages = [],
  url = `${API_BASE_URL}/chat/general`,
}: {
  customer: string
  job: string
  initialMessages?: ChatCompletionMessageParam[]
  url?: string
}) => {
  const snackbar = useSnackbar()  
  const [messages, setMessages] = useState<ChatCompletionMessageParam[]>(initialMessages)
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const send = async (model: string, prompt: string) => {
    const trimmed = prompt.trim()
    if (!trimmed || isLoading) return

    const userMessage: ChatCompletionMessageParam = { role: 'user', content: trimmed }

    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)
    setError(null)

    try {
      const res = await axios.post<ChatCompletionMessageParam[]>(url, {
        customer,
        job,
        messages: [...messages, userMessage],
        model,
      })

      setMessages(res.data)
      setInput('')
    } catch (error) {
      const message = extractErrorMessage(error)
      console.error(message)
      setError(message)
      snackbar.error(message)
      setMessages(messages)
    } finally {
      setIsLoading(false)
    }
  }

  const resetChat = () => {
    setMessages(initialMessages)
    setInput('')
    setError(null)
  }

  return {
    messages,
    setMessages,
    input,
    setInput,
    isLoading,
    error,
    send,
    resetChat,
  }
}

export default useChat